import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router";

import type { OnboardingScreenCreateRequest } from "@/api/types";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { useUploadImage } from "@/hooks/Network/useUploadImage";
import { useSlug } from "@/hooks/useSlug";
import { onboardingScreenMutations } from "@/queries/onboarding-screens/mutations";
import { useOnboardingScreenQueries } from "@/queries/onboarding-screens/queryOptions";
import { useProjectQueries } from "@/queries/project/queryOptions";
import { routes } from "@/routes";

import { FirstOnboardingScreenState } from "./helper";
import { OnboardingScreenForm, type OnboardingScreenFormProps } from "./OnboardingScreenForm";

export function CreateOnboardingScreenPage(): React.ReactNode {
  const slug = useSlug();
  const navigate = useNavigate();
  const { uploadFormImage, isUploadingImage } = useUploadImage();

  const projectQueries = useProjectQueries();
  const onboardingScreenQueries = useOnboardingScreenQueries();
  const {
    data: onboardingScreens,
    isPending: isPendingOnboardingScreens,
    error: onboardingScreensError,
  } = useQuery(onboardingScreenQueries.list());
  const {
    data: projectDetails,
    isPending: isLoadingProjectDetails,
    error: projectDetailsError,
  } = useQuery(projectQueries.details());
  const addOnboardingScreenMutation = onboardingScreenMutations.useAddOnboardingScreen();

  const error = projectDetailsError || onboardingScreensError;
  if (error) {
    return <ErrorPage error={error} />;
  }

  const isLoading = isLoadingProjectDetails || isPendingOnboardingScreens;
  if (isLoading) {
    return <FullSizeLoader withPadding />;
  }

  let firstOnboardingScreenState = FirstOnboardingScreenState.CURRENT;
  if (onboardingScreens[0]) {
    firstOnboardingScreenState =
      onboardingScreens[0].title || onboardingScreens[0]?.subtitle
        ? FirstOnboardingScreenState.HAS_TEXT
        : FirstOnboardingScreenState.NO_TEXT;
  }

  const formProps: OnboardingScreenFormProps = {
    styling: {
      bgColor: projectDetails?.styling.navigationColor,
      textColor: projectDetails?.styling.textLightColor,
    },
    isEditMode: false,
    defaultValues: {
      title: "",
      subtitle: "",
      image: [],
    },
    firstOnboardingScreenState,
    isSubmitting: isUploadingImage || addOnboardingScreenMutation.isPending,
    async onSubmit(data) {
      if (data.image.length === 0) {
        throw new Error("Image not provided");
      }

      const image = await uploadFormImage(data.image[0]);

      if (!image) return;

      const payload: OnboardingScreenCreateRequest = {
        title: data.title,
        subtitle: data.subtitle,
        imageId: image.id,
      };

      await addOnboardingScreenMutation.mutateAsync(payload);

      void navigate(routes.onboardingScreens.list({ slug }));
    },
  };

  return <OnboardingScreenForm {...formProps} />;
}
